import React from 'react';
import clsx from 'clsx';
import Select from 'react-select';
import { withTranslation, WithTranslation } from 'react-i18next';
import SuperuserIsActive from '../../containers/SuperuserIsActive';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { TARGET_TYPES } from '../../constants/config';

type ClickHandler = () => void;

interface TargetActionsPanelOwnProps {
  targetName: string;
  // Save is blocked while either a save or a delete is in flight.
  isTargetSaving: boolean;
  isTargetDeleting: boolean;
  onSave: ClickHandler;

  // Enabled targets cannot be deleted: the two delete buttons are hidden while
  // this is true, and both confirm handlers bail out on it.
  isEnabled: boolean;
  onToggleEnabled: ClickHandler;

  // Index into TARGET_TYPES. The select is uncontrolled — it shows the current
  // type as its PLACEHOLDER rather than its value, which is how it has always
  // worked here.
  targetType: number;
  onChangeType: (option: any) => void;
  // Public page for the current type.
  typePath: string;

  showDeleteDialog: boolean;
  onDeleteClick: ClickHandler;
  onConfirmDelete: ClickHandler;
  onCancelDelete: ClickHandler;
  showDeleteFeedbackDialog: boolean;
  onDeleteFeedbackClick: ClickHandler;
  onConfirmDeleteFeedback: ClickHandler;
  onCancelDeleteFeedback: ClickHandler;

  styles: Record<string, string>;
}

// The editor's action row: save, enable/disable, the type selector with its
// "open" link, and — superuser only — the two delete buttons and their
// confirmation dialogs.
const TargetActionsPanel = ({
  targetName, isTargetSaving, isTargetDeleting, onSave,
  isEnabled, onToggleEnabled, targetType, onChangeType, typePath,
  showDeleteDialog, onDeleteClick, onConfirmDelete, onCancelDelete,
  showDeleteFeedbackDialog, onDeleteFeedbackClick, onConfirmDeleteFeedback,
  onCancelDeleteFeedback, styles, t,
}: TargetActionsPanelOwnProps & WithTranslation) => (
  <div className={clsx(styles['target--button-group'], 'target--button-group')}>
    <div className={clsx(styles['target--button-container'], 'target--button-container')}>
      <button
        className={clsx(styles['save-button'], 'button success target--link')}
        disabled={isTargetSaving || isTargetDeleting}
        onClick={onSave}>
        <i className="fa fa-floppy-o" aria-hidden="true"></i> {t('button.save')}
      </button>

      <button className={clsx(styles['enable-button'], isEnabled ? "button success" : "button alert")}
        type="button"
        title="Enable / Disable target"
        onClick={onToggleEnabled}>
        <i className={isEnabled ? "fa fa-toggle-on" : "fa fa-toggle-off"} aria-hidden="true"></i>
        {isEnabled ? t("target.editor.button.enabled") : t("target.editor.button.disabled")}</button>
    </div>

    <div className={clsx(styles['target--button-container'], 'target--button-container')}>
      <div style={{ marginTop: "5px" }}>
        <Select
          placeholder={TARGET_TYPES[targetType].label}
          id="select_dashboard_type"
          options={TARGET_TYPES}
          onChange={onChangeType}
          styles={{ styles } as any}
        />
      </div>
      <a className={clsx(styles['target--link'], 'button success target--link')}
        href={typePath}
        title="Open feedback page"
        target="_blank">
        <i className="fa fa-eye" aria-hidden="true"></i> {t('button.open')}</a>
    </div>

    <SuperuserIsActive>
      <div className={clsx(styles['target--button-container'], 'target--button-container')}>
        <button className={clsx(isEnabled ? styles.hidden : styles["delete-button"], "button alert")}
          onClick={onDeleteClick}
          disabled={isTargetDeleting}
        >{t('button.delete')}</button>

        <button className={clsx(isEnabled ? styles.hidden : styles["delete-feedback-button"], "button alert")}
          onClick={onDeleteFeedbackClick}
          disabled={isTargetDeleting}
        >{t('button.deleteFeedback')}</button>

        {showDeleteDialog && (
          <ConfirmationDialog
            message={t('target.editor.confirmDelete') + targetName + "?"}
            confirmText={t('button.yes')}
            cancelText={t('button.no')}
            onConfirm={onConfirmDelete}
            onCancel={onCancelDelete}
          />
        )}

        {showDeleteFeedbackDialog && (
          <ConfirmationDialog
            message={t('target.editor.confirmDeleteFeedback') + targetName + "?"}
            confirmText={t('button.yes')}
            cancelText={t('button.no')}
            onConfirm={onConfirmDeleteFeedback}
            onCancel={onCancelDeleteFeedback}
          />
        )}
      </div>
    </SuperuserIsActive>
  </div>
);

export default withTranslation('common')(TargetActionsPanel);
